import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  User, CalendarClock, Map, ClipboardList, Dumbbell, ClipboardCheck, Archive, Target,
  TrendingUp, FileText, Settings as SettingsIcon, LogOut, Menu, X,
  ChevronsLeft, ChevronsRight, BookMarked, LifeBuoy, Layers, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettingsModal } from '../../context/SettingsModalContext';
import { useFeatureVisibility } from '../../context/FeatureVisibilityContext';
import logoIcon from '../../assets/brand/logo-icon.png';
import logoDark from '../../assets/brand/logo-dark.png';

// Persistent left-side navigation for every logged-in student page
// (docs/DECISIONS.md, "Persistent left-side student navigation +
// light-theme visual fix"). Single source of truth for the 13 requested
// menu items - previously this navigation was scattered/duplicated across
// Home.jsx's and CurriculumMenuPage.jsx's own bespoke header dropdowns and
// MegaMenu.jsx's Settings/Logout footer.
const NAV_ITEMS = [
  { key: 'profile', label: 'My Profile', to: '/student-profile', icon: User },
  { key: 'availability', label: 'My Availability', to: '/availability', icon: CalendarClock },
  { key: 'syllabus', label: 'My Syllabus', to: '/my-syllabus', icon: BookMarked },
  { key: 'concept-hub', label: 'Concept Hub', to: '/concept-hub', icon: Layers },
  { key: 'roadmap', label: 'Learning Journey', to: '/roadmap', icon: Map },
  { key: 'study-plan', label: 'Smart Study Plan', to: '/study-plan', icon: ClipboardList },
  // Practice Zone's entry point is /practice (topic picker), which flows
  // into /home (the live question loop) exactly as it does today - both
  // paths count as "active" so the nav doesn't look like it lost track of
  // where the student is once they've moved from browsing into practicing.
  { key: 'practice', label: 'Practice Zone', to: '/practice', icon: Dumbbell, alsoActiveOn: ['/home'] },
  { key: 'assessments', label: 'Assessments', to: '/assessments', icon: ClipboardCheck },
  { key: 'pyq', label: 'Previous Year Papers', to: '/previous-year-questions', icon: Archive, comingSoon: true },
  { key: 'mock-tests', label: 'Mock Tests', to: '/model-exams', icon: Target, comingSoon: true },
  { key: 'progress', label: 'Progress Tracker', to: '/progress', icon: TrendingUp },
  { key: 'reports', label: 'Performance Reports', to: '/reports', icon: FileText },
  { key: 'support', label: 'Support', to: '/support', icon: LifeBuoy },
  // Pilot user feedback (docs/DECISIONS.md, "New feature approved: User
  // Feedback") - admin-disableable after the pilot survey period via the
  // existing Feature Visibility grid (FEATURE_MENU_ITEMS below is derived
  // from this array, so no separate toggle-mechanism change is needed).
  { key: 'feedback', label: 'Feedback', to: '/feedback', icon: MessageSquare },
  { key: 'settings', label: 'Settings', icon: SettingsIcon, action: 'settings' },
];

// Exported so the Admin "Feature Visibility" settings grid
// (src/components/Admin/FeatureVisibilitySettings/) can list every
// toggleable feature by key/label without a second, easy-to-drift copy of
// this list (docs/DECISIONS.md, "Admin-controlled feature visibility per
// class") - editing this array is the ONE place a new menu item needs to
// touch to become admin-toggleable, no backend change required.
//
// ask-lexa/agent-lexa/mentor-support (docs/DECISIONS.md, "Trial Plan: daily
// practice question quota" plan file, Part B) - these 3 aren't sidebar nav
// items (no `to`/route of their own), so they're appended here rather than
// derived from NAV_ITEMS: ask-lexa gates QuestionPanel.jsx's AskLexaPanel,
// agent-lexa gates StudentLayout.jsx's AgentLexaWidget, mentor-support gates
// a real backend rule on mentor allocation (admin_service/lambdas/students/
// handler.py), not a render. "24x7 Support Assistance" reuses the existing
// `support` key above (already toggleable) - no new entry needed for it.
export const FEATURE_MENU_ITEMS = [
  ...NAV_ITEMS.map(({ key, label }) => ({ key, label })),
  { key: 'ask-lexa', label: 'Ask LEXA' },
  { key: 'agent-lexa', label: 'Agent LEXA' },
  { key: 'mentor-support', label: 'Mentor Support' },
];

// Routes where the practice experience (MegaMenu's own curriculum
// drill-down, the question panel, the progress panel) needs the extra
// horizontal room - the sidebar defaults to icon-only here (docs/
// DECISIONS.md, "Persistent left-side student navigation..." follow-up -
// "In the case of Practice Zone... it should be shrunk by default").
const SPACE_NEEDED_ROUTES = ['/practice', '/home'];

function NavItem({ item, isActive, onNavigate, collapsed, onAction }) {
  const Icon = item.icon;
  const className = `flex items-center rounded-btn px-3 py-2.5 text-sm font-medium transition-colors ${
    collapsed ? 'justify-center' : 'gap-3'
  } ${isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`;
  const content = (
    <>
      <Icon size={18} className="flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 text-left">{item.label}</span>
          {item.comingSoon && (
            <span className="flex-shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/60">
              Soon
            </span>
          )}
        </>
      )}
    </>
  );

  // Settings has no route of its own now - it opens a modal instead
  // (docs/DECISIONS.md, Settings-modal decision), so it renders as a
  // button rather than a NavLink.
  if (item.action) {
    return (
      <button
        type="button"
        onClick={() => { onAction(item.action); onNavigate(); }}
        title={collapsed ? item.label : undefined}
        className={`w-full ${className}`}
      >
        {content}
      </button>
    );
  }

  return (
    <NavLink to={item.to} onClick={onNavigate} title={collapsed ? item.label : undefined} className={className}>
      {content}
    </NavLink>
  );
}

export default function StudentSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openSettings } = useSettingsModal();
  const [mobileOpen, setMobileOpen] = useState(false);

  const spaceNeeded = SPACE_NEEDED_ROUTES.includes(location.pathname);
  const [collapsed, setCollapsed] = useState(spaceNeeded);

  // Admin-controlled feature visibility per class + subscription plan
  // (docs/DECISIONS.md, "Admin-controlled feature visibility per class") -
  // fetched once per FeatureVisibilityProvider mount (StudentLayout.jsx),
  // not per sidebar mount (docs/DECISIONS.md, "Trial Plan: daily practice
  // question quota" plan file, Part B - lifted out of this component once
  // Ask LEXA/Agent LEXA gating needed the same data elsewhere).
  const { isVisible } = useFeatureVisibility();

  const visibleNavItems = NAV_ITEMS.filter((item) => isVisible(item.key));

  // Re-defaults every time the student crosses into/out of a
  // space-needed route - a manual expand/collapse made while already on
  // one of these routes (e.g. picking a topic on /practice, landing on
  // /home) is left alone since `spaceNeeded` doesn't change across that
  // transition.
  useEffect(() => {
    setCollapsed(spaceNeeded);
  }, [spaceNeeded]);

  const handleLogout = () => {
    if (!window.confirm('Do you really want to logout?')) return;
    logout();
    navigate('/login');
  };

  const isItemActive = (item) =>
    location.pathname === item.to || (item.alsoActiveOn || []).includes(location.pathname);

  const closeMobile = () => setMobileOpen(false);

  const handleAction = (action) => {
    if (action === 'settings') openSettings();
  };

  const goHome = () => {
    navigate('/practice');
    closeMobile();
  };

  function sidebarContent({ forceExpanded = false } = {}) {
    const isCollapsed = !forceExpanded && collapsed;
    return (
      <div className={`flex h-full flex-shrink-0 flex-col bg-navy transition-[width] duration-200 ${isCollapsed ? 'w-16' : 'w-64'}`}>
        <div className={`flex items-center gap-2 px-4 py-5 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
          {/* Full RUDVAN wordmark whenever the sidebar isn't in its
              narrow/collapsed state (direct user request - the icon-only
              mark alone "is too small... when expanded the full RUDVAN logo
              should come there"). This supersedes an earlier decision that
              kept the desktop-expanded column icon-only to avoid duplicating
              the page's own header wordmark (docs/DECISIONS.md, "Practice
              page header cleanup: single logo..." decision) - some
              duplication on pages with their own bespoke header (Home.jsx/
              CurriculumMenuPage.jsx) is an accepted tradeoff now. Only the
              truly narrow desktop-collapsed state (icon-only column, w-16)
              still shows just the icon mark - it doesn't fit the wordmark. */}
          <button type="button" onClick={goHome} aria-label="Go to home" className="flex-shrink-0">
            {(forceExpanded || !isCollapsed) ? (
              <img src={logoDark} alt="RUDVAN - Adapt. Practice. Master." className="h-9 w-auto" />
            ) : (
              <img src={logoIcon} alt="RUDVAN" className="h-8 w-8" />
            )}
          </button>
          {!isCollapsed && mobileOpen && (
            <button
              type="button"
              onClick={closeMobile}
              className="rounded-btn p-1.5 text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Desktop-only collapse/expand toggle - "shrinkable" menu, moved
            up next to the logo (docs/DECISIONS.md, "Persistent left-side
            student navigation..." follow-up - a bottom-of-page toggle was
            poor UX). Mobile always shows the full drawer since it's
            already an overlay, not competing with page content. */}
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          className={`hidden lg:flex items-center rounded-btn mx-2 mb-2 px-3 py-2 text-white/50 hover:bg-white/10 hover:text-white transition-colors ${isCollapsed ? 'justify-center' : 'justify-end gap-2'}`}
        >
          {isCollapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /><span className="text-xs">Collapse</span></>}
        </button>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 space-y-1">
          {visibleNavItems.map((item) => (
            <NavItem
              key={item.key} item={item} isActive={isItemActive(item)} onNavigate={closeMobile}
              collapsed={isCollapsed} onAction={handleAction}
            />
          ))}
        </nav>

        <div className="border-t border-white/10 px-2 py-3 space-y-1">
          {user && (
            <div className={`flex items-center gap-3 px-3 py-2 ${isCollapsed ? 'justify-center' : ''}`} title={isCollapsed ? (user.name || 'Student') : undefined}>
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-white/70" />
                </div>
              )}
              {!isCollapsed && (
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white truncate">{user.name || 'Student'}</p>
                  <p className="text-[11px] text-white/50 truncate">{user.email}</p>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : undefined}
            className={`flex w-full items-center rounded-btn px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-red-500/20 hover:text-red-300 ${isCollapsed ? 'justify-center' : 'gap-3'}`}
          >
            <LogOut size={18} className="flex-shrink-0" />
            {!isCollapsed && 'Logout'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: floating toggle, only shown while the drawer is closed */}
      {!mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="fixed top-3 left-3 z-[10000] rounded-btn bg-navy p-2 text-white shadow-brand-md lg:hidden"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Desktop: permanent column, collapsible */}
      <div className="hidden lg:block">{sidebarContent()}</div>

      {/* Mobile: slide-in drawer + backdrop, always fully expanded */}
      {mobileOpen && (
        <div className="fixed top-0 right-0 bottom-0 left-0 z-[10000] lg:hidden">
          <div className="absolute top-0 right-0 bottom-0 left-0 bg-black/40" onClick={closeMobile} />
          <div className="absolute top-0 bottom-0 left-0 shadow-brand-lg">{sidebarContent({ forceExpanded: true })}</div>
        </div>
      )}
    </>
  );
}
